import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import styles from "./Product.module.css";
import { useDispatch, useSelector, useStore } from "react-redux"; // Add useStore import
import { useLocation } from "react-router-dom";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

import { useOutletContext } from "react-router-dom";
import { getProduct } from "../../lib/getProduct";

import { addToCart } from "../../lib/addToCart";
import {
  trackEcommerceEvent,
  trackProductCustomization,
} from "../../hooks/useGTM";

import ProductAttributesTable from "./components/ProductAttributesTable/ProductAttributesTable";
import CustomizableOptions from "./components/customizableOptions/customizableOptions";
import LocationModal from "../../components/LocationModal/LocationModal"; // Import LocationModal
import ProductSkeleton from "./components/ProductSkeleton/ProductSkeleton"; // Import the skeleton component

import { getUnifiedDeliveryData } from "../../lib/unifiedDeliveryService"; // Add this import
import DeliveryOptionsContainer from "./components/DeliveryOptionsContainer/DeliveryOptionsContainer"; // Import new component
import ProductSEO from "../../components/SEO/ProductSEO"; // Import ProductSEO component
import ProductImage from "../../components/common/ProductImage/ProductImage"; // Add ProductImage import

interface LocationState {
  customizableOptions?: any[];
  groupingAttributes?: { name: string; value: string }[];
  deliveryLeadTimeZone?: { id: string; name: string }; // Add this line
  deliveryFeeZone?: { id: string; name: string }; // Add this line
}

interface CustomOption {
  value: any;
}

interface ProductAttribute {
  name: string;
  uniqueName: string;
  isGroupingAttribute: boolean;
  groupingAttributeStyle: string | null;
  values?: Array<{
    name: string;
    colorHex?: string | null;
  }>;
}

const Product = () => {
  const { toggleMiniCart } = useOutletContext<{ toggleMiniCart: () => void }>();
  const isMobile = window.innerWidth <= 768;
  const { productUrl } = useParams<{ productUrl: string }>();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [opsLeadTime, setOpsLeadTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedAttributes, setSelectedAttributes] = useState<any>({});
  const [mainImage, setMainImage] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [displayedProduct, setDisplayedProduct] = useState<any>(null);
  const location = useLocation() as { state: LocationState | null };

  const [customOptions, setCustomOptions] = useState<{
    [key: string]: CustomOption;
  }>({});
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false); // State for LocationModal
  const [availableDeliveryMethods, setAvailableDeliveryMethods] = useState<any>(
    {}
  );
  const [deliveryMessage, setDeliveryMessage] = useState<string | null>(null);
  const [isAddToCartDisabled, setIsAddToCartDisabled] =
    useState<boolean>(false);

  const dispatch = useDispatch();
  const store = useStore(); // Initialize store using the useStore hook

  const province = useSelector((state: any) => state.location.province);
  const destinationLeadTimeZoneId = useSelector(
    (state: any) => state.location.deliveryLeadTimeZone?.documentId
  ); // Add this line
  const destinationMethodsZoneId = useSelector(
    (state: any) => state.location.deliveryFeeZone?.documentId
  ); // Add this line

  // Get the destination fee zone ID from Redux
  const destinationFeeZoneId = useSelector(
    (state: any) => state.location.deliveryFeeZone?.documentId
  );

  const fetchProductData = useCallback(async () => {
    if (productUrl) {
      try {
        console.log("Fetching product data for URL:", productUrl);
        const data = await getProduct(productUrl);
        console.log("Product data received:", data);

        // Validate that we received proper data
        if (!data || Object.keys(data).length === 0) {
          console.error("Received empty product data");
          setError("No product data found");
          setLoading(false);
          return;
        }

        setProduct(data);
        setDisplayedProduct(data); // Initial display is the main product
        setMainImage(data.images?.[0]?.small || null);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching product:", err);
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
        setLoading(false);
      }
    }
  }, [productUrl]);

  const fetchDeliveryMethodsAndLeadTime = useCallback(async () => {
    if (
      displayedProduct?.partner?.originZones?.deliveryLeadTimeZone
        ?.documentId &&
      destinationLeadTimeZoneId &&
      destinationMethodsZoneId
    ) {
      try {
        const originLeadTimeZoneId =
          displayedProduct.partner.originZones.deliveryLeadTimeZone.documentId;
        const originFeeZoneId =
          displayedProduct.partner.originZones.deliveryFeeZone.documentId;

        console.log("Fetching unified delivery data:", {
          originLeadTimeZoneId,
          originFeeZoneId,
          destinationLeadTimeZoneId,
          destinationMethodsZoneId,
        });

        // Use unified service to get both lead time and methods in one call
        const deliveryData = await getUnifiedDeliveryData({
          originLeadTimeZoneId,
          originFeeZoneId,
          destinationLeadTimeZoneId,
          destinationFeeZoneId: destinationMethodsZoneId,
        });

        // Use the results
        setAvailableDeliveryMethods(deliveryData.deliveryMethods);
        setOpsLeadTime(deliveryData.opsLeadTime.opsLeadTime);

        console.log("Unified Delivery Data:", deliveryData);

        if (Object.keys(deliveryData.deliveryMethods).length === 0) {
          setDeliveryMessage(
            "No delivery methods available for your district."
          );
          setIsAddToCartDisabled(true);
        } else {
          setDeliveryMessage(null);
          setIsAddToCartDisabled(false);
        }
      } catch (error) {
        console.error("Error fetching delivery data:", error);
        setAvailableDeliveryMethods({});
        setDeliveryMessage("No delivery methods available for your district.");
        setIsAddToCartDisabled(true);
      }
    }
  }, [
    displayedProduct?.partner?.originZones?.deliveryLeadTimeZone?.documentId,
    displayedProduct?.partner?.originZones?.deliveryFeeZone?.documentId,
    destinationLeadTimeZoneId,
    destinationMethodsZoneId,
  ]);

  const calculateDeliveryDates = useCallback(() => {
    if (
      displayedProduct &&
      displayedProduct.partner &&
      displayedProduct.partner.daysOpen &&
      Object.keys(displayedProduct.partner.daysOpen).length > 0 &&
      opsLeadTime !== null
    ) {
      setDeliveryMessage(null);
      setIsAddToCartDisabled(false);
    }
  }, [displayedProduct, opsLeadTime]);

  useEffect(() => {
    fetchProductData();
  }, [fetchProductData]);

  useEffect(() => {
    fetchDeliveryMethodsAndLeadTime();
  }, [fetchDeliveryMethodsAndLeadTime]);

  useEffect(() => {
    calculateDeliveryDates();
  }, [calculateDeliveryDates]);

  useEffect(() => {
    if (
      !product ||
      !product.attributes ||
      Object.keys(selectedAttributes).length === 0
    )
      return;

    const matchingVariant = product.attributes
      ?.filter((attr: any) => attr.isGroupingAttribute)
      .flatMap((attr: any) => attr.values || [])
      .find((value: any) =>
        Object.entries(selectedAttributes).every(
          ([attrName, attrValue]) => value.name === attrValue
        )
      );

    if (matchingVariant) {
      const updatedProduct = {
        ...product,
        id: matchingVariant.id,
        stock: matchingVariant.stock,
        sku: matchingVariant.sku,
        logistics: matchingVariant.logistics || product.logistics,
        price: matchingVariant.price || product.price,
        images: matchingVariant.images?.length
          ? matchingVariant.images
          : product.images,
        additionalAttributes: matchingVariant.additionalAttributes || [],
        customizableOptions: matchingVariant.customizableOptions || [],
      };
      setDisplayedProduct(updatedProduct);
      setMainImage(
        matchingVariant.images?.[0]?.small || product.images?.[0]?.small
      );
      // Update delivery message to use daysToDeliver
      if (updatedProduct.logistics?.daysToDeliver) {
        setDeliveryMessage(
          `Delivery in ${updatedProduct.logistics.daysToDeliver} days`
        );
      }
    } else {
      // Default back to the main product
      setDisplayedProduct(product);
      setMainImage(product.images?.[0]?.small || null);
      setDeliveryMessage(
        `Delivery in ${product.logistics?.minDaysToDeliver}-${product.logistics?.maxDaysToDeliver} days`
      );
    }
  }, [selectedAttributes, product]);

  const handleAttributeSelection = (attrName: string, value: string) => {
    setSelectedAttributes((prevAttributes: any) => ({
      ...prevAttributes,
      [attrName]: value,
    }));
  };

  const handleThumbnailClick = (imageUrl: string) => {
    setMainImage(imageUrl);
  };

  const handleCustomOptionChange = (optionId: string, value: any) => {
    const option = displayedProduct.customizableOptions.find(
      (opt: any) => `${opt.__component}-${opt.id}` === optionId
    );

    if (option.required && !value) {
      alert(`${option.title} es requerido.`);
      return;
    }

    setCustomOptions((prevOptions: any) => ({
      ...prevOptions,
      [optionId]: { value },
    }));

    // Track product customization
    let customizationPrice = 0;

    // Calculate price surcharge based on option type
    if (option) {
      switch (option.__component) {
        case "product-custom-field.single-option":
        case "product-custom-field.dropdown":
          const selectedOption = option.options.find(
            (opt: any) => opt.optionLabel === value
          );
          customizationPrice = selectedOption?.priceSurcharge || 0;
          break;
        case "product-custom-field.multi-select":
          customizationPrice = option.options.reduce(
            (total: number, opt: any) => {
              if (value.includes(opt.optionLabel)) {
                return total + (opt.priceSurcharge || 0);
              }
              return total;
            },
            0
          );
          break;
        default:
          customizationPrice = option.priceSurcharge || 0;
      }
    }

    trackProductCustomization(
      displayedProduct.id,
      displayedProduct.name,
      option.title,
      value,
      customizationPrice
    );
  };

  useEffect(() => {
    if (!location.state) {
      setSelectedAttributes({});
      setCustomOptions({});
    }
  }, [location.state]);

  const generateCartItemID = (
    product: any,
    customOptions: { [key: string]: CustomOption },
    groupingAttributes: any
  ): string => {
    const customizationString = Object.entries(customOptions)
      .map(([key, { value }]) => {
        const option = product.customizableOptions.find(
          (opt: any) => `${opt.__component}-${opt.id}` === key
        );
        return `${option?.title || key}:${String(value)}`;
      })
      .join("|");

    const groupingString = groupingAttributes
      .map((attr: any) => `${attr.name}:${attr.value}`)
      .join("|");

    let sku = `${product.sku}`;
    if (customizationString || groupingString) {
      if (customizationString) sku += `-${customizationString}`;
      if (groupingString) sku += `-${groupingString}`;
    }

    return sku;
  };

  // Add this new function to get required attributes that haven't been selected
  const getRequiredAttributes = (): ProductAttribute[] => {
    return (
      product?.attributes?.filter(
        (attr: ProductAttribute) =>
          attr.isGroupingAttribute && !selectedAttributes[attr.uniqueName]
      ) || []
    );
  };

  // Update hasRequiredAttributesSelected to be more specific
  const hasRequiredAttributesSelected = () => {
    return getRequiredAttributes().length === 0;
  };

  // Update getMissingAttributesMessage to be more user-friendly
  const getMissingAttributesMessage = () => {
    const missingAttributes = getRequiredAttributes();
    if (missingAttributes.length === 0) return "";

    const attributeNames = missingAttributes
      .map((attr) => attr.name)
      .join(", ");
    return `Selecciona ${
      missingAttributes.length === 1 ? "el" : "los"
    } ${attributeNames} para continuar`;
  };

  const handleAddToCart = async () => {
    if (!hasRequiredAttributesSelected()) {
      alert(getMissingAttributesMessage());
      return;
    }

    if (!displayedProduct || quantity < 1 || displayedProduct.stock < 1) {
      alert("Please select valid options or ensure the product is in stock.");
      return;
    }

    console.log("🛠️ Displayed Product:", JSON.stringify(displayedProduct));

    if (!displayedProduct.id) {
      console.error("❌ Missing Product ID!");
      alert("Product ID is missing. Please refresh the page and try again.");
      return;
    }

    // ✅ Prevent Double Click
    if (isAddToCartDisabled) {
      console.warn("🛠️ Add to Cart is already in progress.");
      return;
    }
    setIsAddToCartDisabled(true);

    try {
      // ✅ Validate Required Fields in Customizable Options
      const missingRequiredFields = displayedProduct.customizableOptions?.some(
        (option: any) => {
          const uniqueOptionId = `${option.__component}-${option.id}`;
          const userInput = customOptions[uniqueOptionId]?.value;

          switch (option.__component) {
            case "product-custom-field.multi-select":
              return (
                option.required &&
                (!Array.isArray(userInput) || userInput.length === 0)
              );
            case "product-custom-field.dropdown":
            case "product-custom-field.single-option":
            case "product-custom-field.text-short":
            case "product-custom-field.text-long":
              return option.required && (!userInput || userInput === "");
            default:
              console.error(
                `❌ Unknown component type for validation: ${option.__component}`
              );
              return false;
          }
        }
      );

      if (missingRequiredFields) {
        alert("Please fill in all required fields.");
        return;
      }

      // ✅ Extract Grouping Attributes
      const groupingAttributes =
        product?.attributes
          ?.filter((attr: any) => attr.isGroupingAttribute)
          ?.map((attr: any) => ({
            name: attr.name,
            value: selectedAttributes[attr.uniqueName],
          })) || [];

      console.log("🛠️ Grouping Attributes:", groupingAttributes);

      // ✅ Generate Cart Custom ID
      let cartCustomId = generateCartItemID(
        displayedProduct,
        customOptions,
        groupingAttributes
      );

      if (!cartCustomId || cartCustomId.trim() === "") {
        console.warn("⚠️ Invalid cartCustomId detected, regenerating...");
        cartCustomId = displayedProduct?.sku || `custom-${Date.now()}`;
      }

      console.log("🛠️ Valid Cart Custom ID:", cartCustomId);

      // ✅ Format Customizable Options
      const formattedCustomOptions = Object.entries(customOptions).map(
        ([uniqueOptionId, { value }]) => {
          const option = displayedProduct.customizableOptions.find(
            (opt: any) => `${opt.__component}-${opt.id}` === uniqueOptionId
          );

          let priceSurcharge = 0;

          if (option) {
            switch (option.__component) {
              case "product-custom-field.single-option":
              case "product-custom-field.dropdown":
                const selectedOption = option.options.find(
                  (opt: any) => opt.optionLabel === value
                );
                priceSurcharge = selectedOption?.priceSurcharge || 0;
                break;
              case "product-custom-field.multi-select":
                priceSurcharge = option.options.reduce(
                  (total: number, opt: any) => {
                    if (value.includes(opt.optionLabel)) {
                      return total + (opt.priceSurcharge || 0);
                    }
                    return total;
                  },
                  0
                );
                break;
              default:
                priceSurcharge = option.priceSurcharge || 0;
            }
          }

          return {
            id: uniqueOptionId,
            type: option?.__component || "unknown",
            label: option?.title || "Custom Option",
            value,
            priceSurcharge,
          };
        }
      );

      console.log("🛠️ Formatted Custom Options:", formattedCustomOptions);

      // Create enhanced product data for optimistic UI update
      const enhancedProductData = {
        ...displayedProduct,
        url: productUrl, // Add URL for navigation
        images: displayedProduct.images || product?.images || [], // Ensure we have images
        customizableOptions: formattedCustomOptions,
        groupingAttributes: groupingAttributes,
      };

      // ✅ Add to Cart with enhanced product data and toggle MiniCart immediately
      await addToCart(
        displayedProduct.id.toString(),
        quantity,
        {
          groupingAttributes,
          customizableOptions: formattedCustomOptions,
          cartCustomId: cartCustomId,
          productData: enhancedProductData, // Pass enhanced product data
          destinationZoneId: destinationFeeZoneId, // Pass the destination zone ID from Redux
        },
        dispatch,
        toggleMiniCart, // Pass the toggle function
        store.getState // Pass the getState function from the store
      );

      // Track add to cart event in GTM
      trackEcommerceEvent("add_to_cart", {
        currency: "PEN",
        value:
          (displayedProduct.price?.discount?.newPrice ||
            displayedProduct.price?.regPrice) * quantity,
        items: [
          {
            item_id: displayedProduct.id,
            item_name: displayedProduct.name,
            item_brand: displayedProduct?.partner?.name || "",
            price:
              displayedProduct.price?.discount?.newPrice ||
              displayedProduct.price?.regPrice,
            quantity: quantity,
            item_category: displayedProduct.category?.name || "",
            item_variant:
              groupingAttributes
                .map(
                  (attr: { name: string; value: string }) =>
                    `${attr.name}: ${attr.value}`
                )
                .join(", ") || "",
            customizations:
              formattedCustomOptions.length > 0
                ? JSON.stringify(formattedCustomOptions)
                : undefined,
          },
        ],
      });

      console.log("✅ Product added to cart successfully!");
    } catch (error) {
      console.error("❌ Failed to add product to cart:", error);
      alert(
        `Failed to add product to the cart. ${error || "Please try again."}`
      );
    } finally {
      setIsAddToCartDisabled(false); // ✅ Re-enable button
    }
  };

  const handleLocationModalToggle = () => {
    setIsLocationModalOpen(!isLocationModalOpen);
  };

  // Add a debugging effect to monitor the product data
  useEffect(() => {
    if (product) {
      console.log("Product state updated:", {
        id: product.id,
        name: product.name,
        description: product.description?.substring(0, 50) + "...",
        hasImages: !!product.images?.length,
        firstImageUrl: product.images?.[0]?.small,
        attributes: product.attributes?.length,
      });

      // Track view_item event when product loads
      trackEcommerceEvent("view_item", {
        currency: "PEN",
        value: product.price?.discount?.newPrice || product.price?.regPrice,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            item_brand: product?.partner?.name || "",
            price: product.price?.discount?.newPrice || product.price?.regPrice,
            item_category: product.category?.name || "",
            has_customization_options: product.customizableOptions?.length > 0,
            customization_options_count:
              product.customizableOptions?.length || 0,
          },
        ],
      });
    }
  }, [product]);

  // Add a debugging effect to monitor the displayed product data
  useEffect(() => {
    if (displayedProduct) {
      console.log("Displayed product updated:", {
        id: displayedProduct.id,
        name: displayedProduct.name,
        hasImages: !!displayedProduct.images?.length,
        price: displayedProduct.price,
      });
    }
  }, [displayedProduct]);

  if (loading) return <ProductSkeleton />; // Use the skeleton loader when loading
  if (error) return <div>Error: {error}</div>;

  const thumbnails = [
    ...(displayedProduct !== product ? displayedProduct?.images || [] : []), // Display child product images first
    ...(product?.images || []), // Append main product images
  ];

  console.log("Product Data:", JSON.stringify(displayedProduct));

  return (
    <>
      {/* Add ProductSEO component for SEO metadata with additional checks */}
      {!loading && product && Object.keys(product).length > 0 && (
        <ProductSEO product={product} />
      )}

      <div className="container">
        {loading ? (
          <ProductSkeleton />
        ) : error ? (
          <div className={styles.errorContainer}>
            <h2>Error</h2>
            <p>{error}</p>
          </div>
        ) : !displayedProduct ? (
          <div className={styles.errorContainer}>
            <h2>Producto no encontrado</h2>
            <p>Lo sentimos, no pudimos encontrar el producto que buscas.</p>
          </div>
        ) : (
          <>
            <div className={styles.product}>
              <div className={styles.productImage}>
                <div className={styles.mainImageContainer}>
                  {/* Replace img tag with ProductImage component */}
                  <ProductImage
                    className={styles.mainImage}
                    src={mainImage || undefined} // Convert null to undefined
                    alt={displayedProduct?.name}
                  />
                </div>
                <div className={styles.thumbnails}>
                  {thumbnails.map((img: any, index: number) => (
                    /* Replace img tag with ProductImage component */
                    <ProductImage
                      key={index}
                      className={styles.thumbnail}
                      src={img.small || undefined} // Convert null to undefined
                      alt={`Thumbnail ${index + 1}`}
                      onClick={() => handleThumbnailClick(img.small)}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.productInfo}>
                <div className={styles.productBrand}>
                  {displayedProduct?.partner?.name}
                </div>
                <h1 className={styles.productName}>{displayedProduct?.name}</h1>
                <div className={styles.priceDisplay}>
                  {displayedProduct?.price?.discount ? (
                    <div className={styles.discountedPriceContainer}>
                      <p className={styles.newPrice}>
                        A solo S/{" "}
                        {Number(
                          displayedProduct?.price?.discount?.newPrice
                        ).toFixed(2) || "N/A"}
                      </p>
                      <p className={styles.oldPrice}>
                        S/{" "}
                        {Number(displayedProduct?.price?.regPrice).toFixed(2) ||
                          "N/A"}
                      </p>
                    </div>
                  ) : (
                    <p className={styles.regularPrice}>
                      S/{" "}
                      {Number(displayedProduct?.price?.regPrice).toFixed(2) ||
                        "N/A"}
                    </p>
                  )}
                </div>
                {displayedProduct?.description && (
                  <p>{displayedProduct?.description}</p>
                )}

                {product?.attributes
                  ?.filter((attr: any) => attr.isGroupingAttribute)
                  ?.map((attribute: any) => {
                    // Find selected color name if this is a colorSwatch
                    let selectedColorName = "";
                    if (attribute.groupingAttributeStyle === "colorSwatch") {
                      const selectedValue = attribute.values?.find(
                        (v: any) =>
                          selectedAttributes[attribute.uniqueName] === v.name
                      );
                      selectedColorName = selectedValue
                        ? selectedValue.name
                        : "";
                    }
                    return (
                      <div
                        key={attribute.uniqueName}
                        className={styles.attribute}
                      >
                        <div className={styles.attributeHeader}>
                          <div className={styles.attributeNameContainer}>
                            <p className={styles.attributeName}>
                              {attribute.name}
                              <span className={styles.requiredMark}>*</span>
                              {/* Move the selected color name to appear right next to attribute name */}
                              {attribute.groupingAttributeStyle ===
                                "colorSwatch" &&
                                selectedColorName && (
                                  <span className={styles.selectedColorName}>
                                    : {selectedColorName}
                                  </span>
                                )}
                            </p>
                          </div>
                          {!selectedAttributes[attribute.uniqueName] && (
                            <p className={styles.selectPrompt}>
                              Selecciona una opción
                            </p>
                          )}
                        </div>
                        <div
                          className={
                            attribute.groupingAttributeStyle === "colorSwatch"
                              ? styles.colorSwatches
                              : styles.boxSwatches
                          }
                        >
                          {(attribute.values
                            ? attribute.values // No sorting, keep original order
                            : []
                          ).map((value: any) => (
                            <div
                              key={value.name}
                              className={`${styles.swatchContainer} ${
                                value.stock <= 0 ? styles.outOfStock : ""
                              }`}
                            >
                              <button
                                className={`${
                                  attribute.groupingAttributeStyle ===
                                  "colorSwatch"
                                    ? styles.colorSwatch
                                    : styles.boxSwatch
                                } ${
                                  selectedAttributes[attribute.uniqueName] ===
                                  value.name
                                    ? styles.selectedSwatch
                                    : ""
                                }`}
                                onClick={() =>
                                  value.stock > 0 &&
                                  handleAttributeSelection(
                                    attribute.uniqueName,
                                    value.name
                                  )
                                }
                                disabled={value.stock <= 0}
                              >
                                {attribute.groupingAttributeStyle ===
                                  "colorSwatch" && (
                                  <div
                                    className={styles.colorSwatchInner}
                                    style={{
                                      backgroundColor:
                                        value.colorHex || "transparent",
                                      position: "relative",
                                    }}
                                  ></div>
                                )}
                                {attribute.groupingAttributeStyle !==
                                  "colorSwatch" && <span>{value.name}</span>}
                              </button>
                              <div
                                className={
                                  value.price?.discount?.newPrice
                                    ? styles.variantPriceDiscount
                                    : styles.variantPrice
                                }
                              >
                                {value.price?.discount?.newPrice
                                  ? `S/ ${value.price.discount.newPrice.toFixed(
                                      2
                                    )}`
                                  : `S/ ${
                                      value.price?.regPrice?.toFixed(2) || "N/A"
                                    }`}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                {displayedProduct?.customizableOptions?.length > 0 && (
                  <div className={styles.customizableOptionsContainer}>
                    <h2 className={styles.customizableOptionsTitle}>
                      Personalízalo
                    </h2>
                    <CustomizableOptions
                      options={displayedProduct.customizableOptions.map(
                        (option: any) => {
                          const defaultInputText =
                            option.__component ===
                            "product-custom-field.multi-select"
                              ? [] // Default for multi-select
                              : option.__component ===
                                "product-custom-field.dropdown"
                              ? option.values?.[0] || "" // Default to first dropdown value
                              : ""; // Default for text-short and other types

                          return {
                            ...option,
                            inputText:
                              customOptions[option.id]?.value ??
                              defaultInputText,
                            options: option.options
                              ? [...option.options].sort((a, b) =>
                                  (a.optionLabel || "").localeCompare(
                                    b.optionLabel || "",
                                    undefined,
                                    { sensitivity: "base" }
                                  )
                                )
                              : [], // Ensure options is always sorted
                          };
                        }
                      )}
                      onOptionChange={handleCustomOptionChange}
                      customOptions={customOptions}
                    />
                  </div>
                )}
                {!province && hasRequiredAttributesSelected() ? (
                  <div className={styles.locationPrompt}>
                    <p>
                      Entérate de las opciones y fechas de entrega{" "}
                      <button
                        className="buttonSecondaryOutlined"
                        onClick={handleLocationModalToggle}
                      >
                        Ingresar dirección
                      </button>
                    </p>
                  </div>
                ) : deliveryMessage ? (
                  <div className={styles.deliveryMessage}>
                    {deliveryMessage}
                  </div>
                ) : province ? (
                  <DeliveryOptionsContainer
                    displayedProduct={displayedProduct}
                    availableDeliveryMethods={availableDeliveryMethods}
                    opsLeadTime={opsLeadTime}
                    setDeliveryMessage={setDeliveryMessage}
                    setIsAddToCartDisabled={setIsAddToCartDisabled}
                  />
                ) : null}
                <LocationModal
                  isOpen={isLocationModalOpen}
                  onClose={handleLocationModalToggle}
                />

                <div className={styles.stockAvailability}>
                  {hasRequiredAttributesSelected() ? (
                    // Only show stock status if all required attributes are selected
                    displayedProduct?.stock >= 20 ? (
                      <div className={styles.stockAvailableText}>
                        {/* Replace CheckCircleOutlineIcon with the SVG image */}
                        <div className={styles.stockIcon}>
                          <img
                            src="https://media.evefant.com/green_check_icon_evf_43f85b4028.svg"
                            alt="In Stock"
                            width="24"
                            height="24"
                          />
                        </div>
                        <p>En stock</p>
                      </div>
                    ) : displayedProduct?.stock > 0 ? (
                      <div className={styles.stockLowText}>
                        <WarningAmberIcon className={styles.stockIcon} />
                        <p>
                          ¡Apúrate! Solo quedan {displayedProduct.stock}{" "}
                          unidades
                        </p>
                      </div>
                    ) : (
                      <div className={styles.stockOutText}>
                        <ErrorOutlineIcon className={styles.stockIcon} />
                        <p>Sin stock</p>
                      </div>
                    )
                  ) : null}
                </div>

                {displayedProduct?.stock > 0 && (
                  <div className={styles.quantityAddToCartContainer}>
                    {!hasRequiredAttributesSelected() && (
                      <p className={styles.attributeWarning}>
                        {getMissingAttributesMessage()}
                      </p>
                    )}
                    {isMobile ? (
                      <>
                        <div className={styles.mobilePrice}>
                          <span className={styles.mobilePriceLabel}>
                            Total:
                          </span>
                          <span className={styles.mobilePriceValue}>
                            S/{" "}
                            {(
                              (displayedProduct?.price?.discount?.newPrice ||
                                displayedProduct?.price?.regPrice) * quantity
                            ).toFixed(2)}
                          </span>
                        </div>
                        <div className={styles.mobileActions}>
                          <div className={styles.quantitySelector}>
                            <button
                              className={styles.quantityButton}
                              onClick={() =>
                                setQuantity((prev) => Math.max(1, prev - 1))
                              }
                              disabled={quantity <= 1}
                              aria-label="Decrease quantity"
                            >
                              <RemoveIcon style={{ fontSize: "22px" }} />
                            </button>
                            <div className={styles.quantityDisplay}>
                              {quantity}
                            </div>
                            <button
                              className={styles.quantityButton}
                              onClick={() =>
                                setQuantity((prev) =>
                                  Math.min(prev + 1, displayedProduct?.stock)
                                )
                              }
                              disabled={quantity >= displayedProduct?.stock}
                              aria-label="Increase quantity"
                            >
                              <AddIcon style={{ fontSize: "22px" }} />
                            </button>
                          </div>
                          <button
                            className="buttonMain"
                            onClick={handleAddToCart}
                            disabled={
                              quantity < 1 ||
                              displayedProduct?.stock < 1 ||
                              isAddToCartDisabled ||
                              !hasRequiredAttributesSelected()
                            }
                          >
                            <ShoppingCartOutlinedIcon
                              style={{ fontSize: "18px" }}
                            />
                            Añadir
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={styles.quantitySelector}>
                          <button
                            className={styles.quantityButton}
                            onClick={() =>
                              setQuantity((prev) => Math.max(1, prev - 1))
                            }
                            disabled={quantity <= 1}
                          >
                            <RemoveIcon />
                          </button>
                          <div className={styles.quantityDisplay}>
                            {quantity}
                          </div>
                          <button
                            className={styles.quantityButton}
                            onClick={() =>
                              setQuantity((prev) =>
                                Math.min(prev + 1, displayedProduct?.stock)
                              )
                            }
                            disabled={quantity >= displayedProduct?.stock}
                          >
                            <AddIcon />
                          </button>
                        </div>
                        <button
                          className="buttonMain"
                          onClick={handleAddToCart}
                          disabled={
                            quantity < 1 ||
                            displayedProduct?.stock < 1 ||
                            isAddToCartDisabled ||
                            !hasRequiredAttributesSelected()
                          }
                        >
                          Agregar al Carrito
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.attributesTableContainer}>
              <ProductAttributesTable
                attributes={[
                  ...(product?.attributes?.filter(
                    (attr: any) => !attr.isGroupingAttribute
                  ) || []),
                  ...(displayedProduct?.additionalAttributes || []),
                ]}
                sku={displayedProduct?.sku}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Product;
